import IconBasicData from "assets/img/icon-basic-data.svg?react"
import IconOrganisationalAttributes from "assets/img/icon-organisational-attributes.svg?react"
import IconTechnicalData from "assets/img/icon-technical-data.svg?react"
import IconFeatures from "assets/img/icon-features.svg?react"
import IconLocation from "assets/img/icon-location.svg?react"
import IconFinance from "assets/img/icon-finance.svg?react"

import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { ArticleAny } from "@/types"
import Asset from "models/Asset"
import { Switch, SwitchOption } from "components/Switch"
import AssetPinnedFields from "../AssetSidebar/AssetPinnedFields"
import { Flex, FlexItem } from "components/Flex"
import { useAssetPinnedFields } from "stores/assetPinnedFields"
import { useTenantSettings } from "stores/tenantSettings"
import useAssetInitialSaleOrRental from "hooks/useAssetInitialSaleOrRental"
import { ArticleType } from "config/constants"
import useAssetColor from "pages/AssetPage/hooks/useAssetColor"
import AssetData from "components/AssetData/AssetData"
import { AssetStep } from "pages/AssetPage/config"
import Routes from "routes/index"
import useAssetCollection from "hooks/useAssetCollection"
import AssetDisplayStatus from "models/AssetDisplayStatus"
import Address from "models/Address"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import { IAssetInternalNotesEdit } from "components/AssetData/AssetDataContext"


interface IAssetSidebarAssetDataProps {
    asset: Asset
    article: ArticleAny
    displayStatus?: AssetDisplayStatus
    displayStatusLoading: boolean
    operationHoursLogButton: ReactNode
    eventAddress?: Address
}

const AssetSidebarAssetData: FC<IAssetSidebarAssetDataProps> = ({
    asset,
    article,
    displayStatus,
    displayStatusLoading,
    operationHoursLogButton,
    eventAddress,
}) => {
    const { t } = useTranslation()
    const { pinnedFields, fetchPinnedFields } = useAssetPinnedFields()
    const [activeTab, setActiveTab] = useState<AssetStep | null>(null)
    const { assetCollection, fetchAssetCollection } = useAssetCollection(asset.linkGetAssetCollection)

    useEffect(() => {
        if (pinnedFields) return
        fetchPinnedFields()
    }, [fetchPinnedFields, pinnedFields])

    useEffect(() => {
        fetchAssetCollection()
    }, [fetchAssetCollection])

    const {
        tenantWarehouses, fetchTenantWarehouses,
        tenantEmployees, fetchTenantEmployees,
    } = useTenantSettings()

    useEffect(() => {
        if (!tenantWarehouses) {
            fetchTenantWarehouses()
        }
    }, [fetchTenantWarehouses, tenantWarehouses])

    useEffect(() => {
        if (!tenantEmployees) {
            fetchTenantEmployees()
        }
    }, [fetchTenantEmployees, tenantEmployees])

    const homebaseWarehouse = useMemo(() => {
        if (!asset?.warehouseId || !tenantWarehouses) return null
        return tenantWarehouses?.entities.find(w => w.id === asset.warehouseId) || null
    }, [asset?.warehouseId, tenantWarehouses])

    const homebaseWarehouseDescription = useMemo(() => {
        if (!asset) return null
        if (!asset.warehouseId) return
        if (!tenantWarehouses) return null
        if (asset.warehouseId === tenantWarehouses.noWarehouseId) return t("common.noWarehouse")
        return homebaseWarehouse?.description || null
    }, [asset, homebaseWarehouse?.description, t, tenantWarehouses])


    const initialSaleOrRental = useAssetInitialSaleOrRental({
        isVisible: article?.articleType === ArticleType.machine,
    })

    useEffect(() => {
        initialSaleOrRental.setRelevantForAssetStatistic(asset.assetRequestStatistic?.relevantForAssetStatistic || false)
        initialSaleOrRental.setReportAsset(asset.assetRequestStatistic?.reportAsset || false)
        initialSaleOrRental.setReservationId(asset.assetRequestStatistic?.reservationId || null)
        initialSaleOrRental.setChangeUserId(asset.assetRequestStatistic?.changeUserId || null)
        initialSaleOrRental.setChangeDate(asset.assetRequestStatistic?.changeDate || null)
        initialSaleOrRental.setLocked(asset.assetRequestStatistic?.locked || false)
    }, [asset, initialSaleOrRental])

    const {
        color,
        initColorById,
    } = useAssetColor()

    useEffect(() => {
        if (asset?.colorId) {
            initColorById(asset.colorId)
        }
    }, [asset?.colorId, initColorById])

    const handleInternalNotesSaveImpl = useCallback(async (notes: string) => {
        if (!asset.linkUpdate || !asset.id) return
        try {
            await AssetService.updateAsset(asset.linkUpdate, { ...asset.toDto(), internalNotes: notes }, asset.eTag ?? "")
            asset.internalNotes = notes
            // Re-fetch to get the server's new eTag, preventing 409 on subsequent saves.
            const refreshed = await AssetService.getAsset(asset.id, { withFinancial: false, withDisposalEmail: false })
            if (refreshed?.eTag) asset.eTag = refreshed.eTag
            if (refreshed?.linkUpdate) asset.linkUpdate = refreshed.linkUpdate
            toastMessages.showDataSaved()
        } catch {
            toastMessages.showSystemError()
        }
    }, [asset])

    const [notesEditContext, setNotesEditContext] = useState<'pinned' | 'section' | null>(null)
    const [savedNotes, setSavedNotes] = useState(asset.internalNotes || '')
    const [notesValue, setNotesValue] = useState(asset.internalNotes || '')
    const skipBlurRef = useRef(false)

    const startNotesEdit = useCallback((context: 'pinned' | 'section') => {
        setNotesEditContext(context)
        setNotesValue(savedNotes)
    }, [savedNotes])

    const handleNotesSave = useCallback(() => {
        setNotesEditContext(null)
        setSavedNotes(notesValue)
        handleInternalNotesSaveImpl(notesValue)
    }, [notesValue, handleInternalNotesSaveImpl])

    const handleNotesCancel = useCallback(() => {
        setNotesEditContext(null)
        setNotesValue(savedNotes)
    }, [savedNotes])

    const handleNotesBlur = useCallback(() => {
        if (skipBlurRef.current) {
            skipBlurRef.current = false
            return
        }
        if (notesEditContext) handleNotesSave()
    }, [notesEditContext, handleNotesSave])

    const onNotesToolbarPointerDown = useCallback(() => {
        skipBlurRef.current = true
    }, [])

    const internalNotesEdit: IAssetInternalNotesEdit = useMemo(() => ({
        canEdit: !!asset.linkUpdate,
        editingContext: notesEditContext,
        savedNotes,
        notesValue,
        setNotesValue,
        startEdit: startNotesEdit,
        handleSave: handleNotesSave,
        handleCancel: handleNotesCancel,
        handleBlur: handleNotesBlur,
        onToolbarPointerDown: onNotesToolbarPointerDown,
    }), [asset.linkUpdate, notesEditContext, savedNotes, notesValue, startNotesEdit, handleNotesSave, handleNotesCancel, handleNotesBlur, onNotesToolbarPointerDown])

    return (
        <>
            <Flex flow="column" gap="3.2rem">
                <AssetPinnedFields
                    asset={asset}
                    article={article}
                    connectedAssets={assetCollection?.assets}
                    displayStatus={displayStatus}
                    displayStatusLoading={displayStatusLoading}
                    homebaseWarehouseDescription={homebaseWarehouseDescription}
                    initialSaleOrRental={initialSaleOrRental}
                    operationHoursLogButton={operationHoursLogButton}
                    color={color}
                    eventAddress={eventAddress}
                    internalNotesEdit={internalNotesEdit}
                />
                <FlexItem>
                    <Switch
                        value={activeTab}
                        onSwitch={setActiveTab}
                        theme="square"
                        fullWidth
                        className="mb-3">
                        <SwitchOption value={null}>
                            {t('common.all')}
                        </SwitchOption>
                        <SwitchOption value={AssetStep.basicData} tooltip={t('asset.basicData')}>
                            <IconBasicData />
                        </SwitchOption>
                        <SwitchOption value={AssetStep.organisationalAttributes} tooltip={t('asset.organisationalAttributes')}>
                            <IconOrganisationalAttributes />
                        </SwitchOption>
                        <SwitchOption value={AssetStep.technicalData} tooltip={t('asset.technicalData')}>
                            <IconTechnicalData />
                        </SwitchOption>
                        <SwitchOption value={AssetStep.features} tooltip={t('asset.features')}>
                            <IconFeatures />
                        </SwitchOption>
                        <SwitchOption value={AssetStep.location} tooltip={t('asset.location')}>
                            <IconLocation />
                        </SwitchOption>
                        {asset.hasFinancialData &&
                            <SwitchOption value={AssetStep.finance} tooltip={t('asset.finance')}>
                                <IconFinance />
                            </SwitchOption>
                        }
                    </Switch>

                    <AssetData
                        asset={asset}
                        article={article}
                        operationHoursLogButton={operationHoursLogButton}
                        section={activeTab ?? undefined}
                        tenantEmployees={tenantEmployees?.entities}
                        initialSaleOrRental={initialSaleOrRental}
                        color={color}
                        eventAddress={eventAddress}
                        connectedAssets={assetCollection?.assets}
                        displayStatus={displayStatus}
                        displayStatusLoading={displayStatusLoading}
                        homebaseWarehouse={homebaseWarehouse}
                        homebaseWarehouseDescription={homebaseWarehouseDescription}
                        getEditLink={asset.id && asset.linkUpdate
                            ? step => Routes.assetEdit.getPath({ assetId: asset.id! }, { backUrl: location.pathname + location.search, step })
                            : undefined
                        }
                        internalNotesEdit={internalNotesEdit}
                        pinnable
                        placement="sidebar"
                        copyFeatures
                    />
                </FlexItem>
            </Flex>
        </>
    )
}

export default AssetSidebarAssetData
